/**
 * TUVI - Contratos Component
 * Gestión de contratos de suministro e instalación
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import CotizacionPDF from './CotizacionPDF';
import EditorRico from './EditorRico';
import ClausulasPaginadas from './ClausulasPaginadas';
import { CLAUSULAS_CONTRATO_DEFAULT } from './plantillaContratoDefault';
import './Contratos.css';

/**
 * Forma de pago según el TIPO DE CONTRATO.
 * Única fuente de verdad: la usan el formulario, la validación de la suma,
 * la tabla del documento generado y la cláusula de forma de pago.
 *   Tipo A: 5 pagos (10/40/20/20/10)
 *   Tipo B: 4 pagos (10/40/30/20)
 * `key` debe coincidir con los campos pct_<key> / valor_<key> del formulario.
 */
const FORMA_PAGO = {
  A: [
    { key: 'separacion', letra: 'A', label: 'Separación', pct: 10, desc: 'Pago acordado para la separación del valor del contrato.' },
    { key: 'inicio_obra', letra: 'B', label: 'Inicio de obra', pct: 40, desc: 'Valor que debe estar cancelado para dar inicio a la obra.' },
    { key: 'avance_obra', letra: 'C', label: 'Avance de obra 1', pct: 20, desc: 'Pago a realizar el día 20 después del inicio de obra (semana 3).' },
    { key: 'saldo_final', letra: 'D', label: 'Avance de obra 2', pct: 20, desc: 'Pago a realizar el día 40 después del inicio de obra (semana 6).' },
    { key: 'entrega', letra: 'E', label: 'Recibo a conformidad', pct: 10, desc: 'Acta de entrega firmada.' },
  ],
  B: [
    { key: 'separacion', letra: 'A', label: 'Separación', pct: 10, desc: 'Pago acordado para la separación del valor del contrato.' },
    { key: 'inicio_obra', letra: 'B', label: 'Inicio de obra', pct: 40, desc: 'Valor que debe estar cancelado para dar inicio a la obra.' },
    { key: 'avance_obra', letra: 'C', label: 'Avance de obra', pct: 30, desc: 'Pago a realizar el día 20 después del inicio de obra (semana 3).' },
    { key: 'saldo_final', letra: 'D', label: 'Programar fecha de entrega', pct: 20, desc: 'Pago a realizar cuando la obra esté a una semana de completarse para agendar una fecha de entrega.' },
  ],
};
// Todas las claves posibles (para poner en 0 las que no usa el tipo seleccionado)
const CLAVES_PAGO = ['separacion', 'inicio_obra', 'avance_obra', 'saldo_final', 'entrega'];

// Función para obtener fecha actual en zona horaria de Colombia
const getFechaColombia = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
};

// Función para convertir número a letras
const numeroALetras = (numero) => {
  const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const especiales = ['ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  const convertirGrupo = (n) => {
    if (n === 0) return '';
    if (n === 100) return 'CIEN';

    let resultado = '';
    const c = Math.floor(n / 100);
    const resto = n % 100;

    if (c > 0) resultado += centenas[c] + ' ';

    if (resto > 0) {
      if (resto < 10) {
        resultado += unidades[resto];
      } else if (resto >= 11 && resto <= 19) {
        resultado += especiales[resto - 11];
      } else if (resto === 10) {
        resultado += 'DIEZ';
      } else if (resto === 20) {
        resultado += 'VEINTE';
      } else if (resto >= 21 && resto <= 29) {
        resultado += 'VEINTI' + unidades[resto - 20];
      } else {
        const d = Math.floor(resto / 10);
        const u = resto % 10;
        resultado += decenas[d];
        if (u > 0) resultado += ' Y ' + unidades[u];
      }
    }
    return resultado.trim();
  };

  if (!numero || isNaN(numero)) return '';

  const num = Math.floor(Math.abs(parseFloat(numero)));
  if (num === 0) return 'CERO PESOS M/CTE';

  const millones = Math.floor(num / 1000000);
  const miles = Math.floor((num % 1000000) / 1000);
  const unidad = num % 1000;

  let resultado = '';

  if (millones > 0) {
    if (millones === 1) {
      resultado += 'UN MILLÓN ';
    } else {
      resultado += convertirGrupo(millones) + ' MILLONES ';
    }
  }

  if (miles > 0) {
    if (miles === 1) {
      resultado += 'MIL ';
    } else {
      resultado += convertirGrupo(miles) + ' MIL ';
    }
  }

  if (unidad > 0) {
    resultado += convertirGrupo(unidad);
  }

  return resultado.trim() + ' PESOS M/CTE';
};

const Contratos = () => {
  const { success, error } = useNotification();
  const { tienePermiso } = useAuth();
  const puedeEditarPlantilla = tienePermiso('documentos', 'contratos-editar-plantilla');
  // Plantilla global abierta en el editor (A o B). Es independiente del tipo del
  // contrato que se esté armando: desde el listado se puede editar cualquiera.
  const [plantillaTipo, setPlantillaTipo] = useState('A');
  const abrirEditorPlantilla = (tipo) => { setPlantillaTipo(tipo === 'B' ? 'B' : 'A'); setEditorAbierto('plantilla'); };

  // Plantillas GLOBALES de cláusulas por tipo de contrato (null = usar la del código).
  const [plantillaA, setPlantillaA] = useState(null);
  const [plantillaB, setPlantillaB] = useState(null);
  // Cuerpo propio del contrato en edición (override de la plantilla; null = usa la global)
  const [cuerpoContrato, setCuerpoContrato] = useState(null);
  // Editor abierto: 'plantilla' (global) | 'contrato' (individual) | null
  const [editorAbierto, setEditorAbierto] = useState(null);
  const [guardandoEditor, setGuardandoEditor] = useState(false);
  const documentRef = useRef(null);

  const [vista, setVista] = useState('lista');
  // Modal que pregunta el tipo de contrato antes de abrir el formulario
  const [modalTipoAbierto, setModalTipoAbierto] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contratoEditando, setContratoEditando] = useState(null);

  // Estado de búsqueda para lista principal
  const [busquedaLista, setBusquedaLista] = useState('');

  // Estado para PDF con cotización anexa
  const [cotizaciones, setCotizaciones] = useState([]);
  const [showCotizacionModal, setShowCotizacionModal] = useState(false);
  const [busquedaCotizacion, setBusquedaCotizacion] = useState('');
  const [cotizacionPDFData, setCotizacionPDFData] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const cotizacionPdfRef = useRef(null);

  const [formData, setFormData] = useState({
    fecha: getFechaColombia(),
    proyecto_id: '',
    nombre_cliente: '',
    cedula_cliente: '',
    direccion_cliente: '',
    telefono_cliente: '',
    correo_cliente: '',
    proyecto_nombre: '',
    apartamento: '',
    direccion_proyecto: '',
    valor_contrato: '',
    pct_separacion: 10,
    pct_inicio_obra: 40,
    pct_avance_obra: 20,
    pct_saldo_final: 20,
    pct_entrega: 10,
    valor_separacion: '',
    valor_inicio_obra: '',
    valor_avance_obra: '',
    valor_saldo_final: '',
    valor_entrega: '',
    plazo_ejecucion: '60',
    tipo_contrato: 'A'
  });

  const valorContratoLetras = numeroALetras(formData.valor_contrato);

  // Ítems de pago del tipo de contrato seleccionado (A por defecto)
  const pagosDelTipo = FORMA_PAGO[formData.tipo_contrato] || FORMA_PAGO.A;

  // Elegir el tipo desde el modal y abrir el formulario de creación ya configurado
  const iniciarContrato = (tipo) => {
    resetForm();
    handleTipoContratoChange(tipo);
    setModalTipoAbierto(false);
    setVista('crear');
  };

  // Cambiar de tipo: aplica los porcentajes de ese tipo, recalcula los valores
  // sobre el valor del contrato y pone en 0 los campos que el tipo no usa.
  const handleTipoContratoChange = (nuevoTipo) => {
    const items = FORMA_PAGO[nuevoTipo] || FORMA_PAGO.A;
    setFormData(prev => {
      const total = parseFloat(prev.valor_contrato) || 0;
      const next = { ...prev, tipo_contrato: nuevoTipo };
      CLAVES_PAGO.forEach(k => {
        const item = items.find(i => i.key === k);
        next[`pct_${k}`] = item ? item.pct : 0;
        next[`valor_${k}`] = item ? Math.round(total * item.pct / 100) : 0;
      });
      return next;
    });
  };

  const abortRef = useRef(null);
  useEffect(() => {
    cargarContratos();
    cargarProyectos();
    cargarCotizaciones();
    cargarPlantilla();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const cargarPlantilla = async () => {
    try {
      const data = await client.query(`{ a: plantillaContrato(tipo: "A") b: plantillaContrato(tipo: "B") }`);
      setPlantillaA(data.a || null);
      setPlantillaB(data.b || null);
    } catch (e) { /* usa la plantilla por defecto */ }
  };

  // Cuerpo de cláusulas efectivo para la vista previa: override del contrato →
  // Plantilla global según el tipo del contrato (A o B) → default del código.
  const plantillaGlobal = formData.tipo_contrato === 'B' ? plantillaB : plantillaA;
  const cuerpoEfectivo = cuerpoContrato || plantillaGlobal || CLAUSULAS_CONTRATO_DEFAULT;

  // Cuerpo que se imprime: ajusta la cláusula FORMA DE PAGO al tipo de contrato.
  // (El editor sigue trabajando sobre `cuerpoEfectivo`, el texto guardado tal cual.)
  const cuerpoImpreso = React.useMemo(() => {
    let html = cuerpoEfectivo || '';
    const items = pagosDelTipo
      .map(i => `<p><strong>${i.letra}. ${i.label}:</strong> ${i.desc}</p>`)
      .join('');
    // Sustituir el listado de pagos existente (va desde "A. Separación" hasta el PARÁGRAFO)
    const reListado = /<p><strong>A\.\s*Separaci[óo]n:[\s\S]*?(?=<p><strong>PAR[ÁA]GRAFO)/i;
    if (reListado.test(html)) html = html.replace(reListado, items);
    // La plantilla trae "SÉPTIMA" repetida (Fecha de inicio de obra y Forma de pago).
    // La cláusula de forma de pago es la OCTAVA.
    html = html.replace(/<u>S[ÉE]PTIMA:<\/u>\s*FORMA DE PAGO/i, '<u>OCTAVA:</u> FORMA DE PAGO');
    return html;
  }, [cuerpoEfectivo, pagosDelTipo]);

  // Guardar desde el editor (plantilla global o contrato individual)
  const guardarDesdeEditor = async (html) => {
    setGuardandoEditor(true);
    try {
      if (editorAbierto === 'plantilla') {
        const tipo = plantillaTipo === 'B' ? 'B' : 'A';
        const r = await client.mutate(`mutation($html: String!, $tipo: String) { guardarPlantillaContrato(contenido_html: $html, tipo: $tipo) { success message } }`, { html, tipo });
        if (r.guardarPlantillaContrato.success) {
          if (tipo === 'B') setPlantillaB(html); else setPlantillaA(html);
          success(`Plantilla de contratos (Tipo ${tipo}) actualizada`);
          setEditorAbierto(null);
        } else { error(r.guardarPlantillaContrato.message); }
      } else if (editorAbierto === 'contrato' && contratoEditando) {
        const r = await client.mutate(`mutation($id: Int!, $html: String) { actualizarCuerpoContrato(id: $id, cuerpo_html: $html) { success message } }`, { id: contratoEditando.id, html });
        if (r.actualizarCuerpoContrato.success) {
          setCuerpoContrato(html);
          success('Contrato actualizado');
          setEditorAbierto(null);
        } else { error(r.actualizarCuerpoContrato.message); }
      }
    } catch (e) {
      error('Error al guardar');
    } finally {
      setGuardandoEditor(false);
    }
  };

  // Calcular valores automáticamente cuando cambia el valor del contrato
  useEffect(() => {
    if (formData.valor_contrato) {
      const total = parseFloat(formData.valor_contrato);
      setFormData(prev => ({
        ...prev,
        valor_separacion: Math.round(total * prev.pct_separacion / 100),
        valor_inicio_obra: Math.round(total * prev.pct_inicio_obra / 100),
        valor_avance_obra: Math.round(total * prev.pct_avance_obra / 100),
        valor_saldo_final: Math.round(total * prev.pct_saldo_final / 100),
        valor_entrega: Math.round(total * prev.pct_entrega / 100)
      }));
    }
  }, [formData.valor_contrato]);

  const cargarContratos = async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const data = await client.query(`{ contratos { id consecutivo fecha proyecto_id nombre_cliente cedula_cliente direccion_cliente telefono_cliente correo_cliente proyecto_nombre apartamento direccion_proyecto valor_contrato valor_separacion valor_inicio_obra valor_avance_obra valor_saldo_final valor_entrega plazo_ejecucion tipo_contrato cuerpo_html created_at } }`);
      setContratos(data.contratos || []);
    } catch (err) {
      if (err.name !== 'AbortError') error('Error al cargar contratos');
    } finally {
      setLoading(false);
    }
  };

  const cargarProyectos = async () => {
    try {
      const data = await client.query(`{ proyectos { id proyecto_nombre nombre_cliente apartamento direccion cedula telefono correo valor_final } }`);
      setProyectos(data.proyectos || []);
    } catch (err) {
      console.error('Error cargando proyectos:', err);
    }
  };

  const cargarCotizaciones = async () => {
    try {
      // incluir_especiales: desde una cotización especial también se hace contrato
      const data = await client.query(`{ cotizaciones(limit: 9999, incluir_especiales: true) { cotizaciones { id consecutivo nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento direccion valor_final descuento estado } } }`);
      setCotizaciones(data.cotizaciones.cotizaciones || []);
    } catch (err) {
      console.error('Error cargando cotizaciones:', err);
    }
  };

  const handleProyectoChange = (e) => {
    const proyectoId = e.target.value;
    const proyecto = proyectos.find(p => p.id === parseInt(proyectoId));

    setFormData(prev => {
      const next = { ...prev, proyecto_id: proyectoId };
      if (proyecto) {
        // Autocompletar SOLO los campos vacíos: así, al re-vincular un contrato ya
        // diligenciado, se cambia el proyecto sin pisar los datos del cliente/valor.
        if (!prev.nombre_cliente) next.nombre_cliente = proyecto.nombre_cliente || '';
        if (!prev.cedula_cliente) next.cedula_cliente = proyecto.cedula || '';
        if (!prev.proyecto_nombre) next.proyecto_nombre = proyecto.proyecto_nombre || '';
        if (!prev.apartamento) next.apartamento = proyecto.apartamento || '';
        if (!prev.direccion_proyecto) next.direccion_proyecto = proyecto.direccion || '';
        if (!prev.valor_contrato) next.valor_contrato = proyecto.valor_final || '';
      }
      return next;
    });
  };

  // Copiar datos de una cotización al formulario del contrato
  const [busquedaCotForm, setBusquedaCotForm] = useState('');
  const [showCotFormDropdown, setShowCotFormDropdown] = useState(false);

  const cotizacionesFormFiltradas = cotizaciones.filter(c => {
    if (!busquedaCotForm) return false;
    const t = busquedaCotForm.toLowerCase();
    return (c.nombre_cliente || '').toLowerCase().includes(t) ||
           (c.proyecto_nombre || '').toLowerCase().includes(t) ||
           (c.apartamento || '').toLowerCase().includes(t) ||
           String(c.consecutivo).includes(t);
  }).slice(0, 10);

  const handleCopiarCotizacion = (cot) => {
    setFormData(prev => ({
      ...prev,
      cotizacion_id: cot.id, // puente: de qué cotización se copió (para vincular al proyecto)
      nombre_cliente: cot.nombre_cliente || prev.nombre_cliente,
      cedula_cliente: cot.cedula_cliente || prev.cedula_cliente,
      telefono_cliente: cot.telefono_cliente || prev.telefono_cliente,
      correo_cliente: cot.correo_cliente || prev.correo_cliente,
      proyecto_nombre: cot.proyecto_nombre || prev.proyecto_nombre,
      apartamento: cot.apartamento || prev.apartamento,
      direccion_proyecto: cot.direccion || prev.direccion_proyecto,
      // Toma el valor CON descuento aplicado (si la cotización tiene descuento)
      valor_contrato: Math.max(0, (parseFloat(cot.valor_final) || 0) - (parseFloat(cot.descuento) || 0)) || prev.valor_contrato,
    }));
    setBusquedaCotForm('');
    setShowCotFormDropdown(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePctChange = (pctField, newValue) => {
    const pctValue = Math.max(0, Math.min(100, parseFloat(newValue) || 0));
    const valorField = pctField.replace('pct_', 'valor_');
    setFormData(prev => {
      const total = parseFloat(prev.valor_contrato) || 0;
      return {
        ...prev,
        [pctField]: pctValue,
        [valorField]: Math.round(total * pctValue / 100)
      };
    });
  };

  const handleValorChange = (valorField, newValue) => {
    const valorNum = Math.max(0, parseFloat(newValue) || 0);
    const pctField = valorField.replace('valor_', 'pct_');
    setFormData(prev => {
      const total = parseFloat(prev.valor_contrato) || 0;
      return {
        ...prev,
        [valorField]: valorNum,
        [pctField]: total > 0 ? Math.round(valorNum / total * 100) : prev[pctField]
      };
    });
  };

  const resetForm = () => {
    setFormData({
      fecha: getFechaColombia(),
      proyecto_id: '',
      nombre_cliente: '',
      cedula_cliente: '',
      direccion_cliente: '',
      telefono_cliente: '',
      correo_cliente: '',
      proyecto_nombre: '',
      apartamento: '',
      direccion_proyecto: '',
      valor_contrato: '',
      pct_separacion: 10,
      pct_inicio_obra: 40,
      pct_avance_obra: 20,
      pct_saldo_final: 20,
      pct_entrega: 10,
      valor_separacion: '',
      valor_inicio_obra: '',
      valor_avance_obra: '',
      valor_saldo_final: '',
      valor_entrega: '',
      plazo_ejecucion: '60',
      tipo_contrato: 'A'
    });
    setContratoEditando(null);
    setCuerpoContrato(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const input = {
        fecha: formData.fecha, proyecto_id: formData.proyecto_id ? parseInt(formData.proyecto_id) : null,
        cotizacion_id: formData.cotizacion_id ? parseInt(formData.cotizacion_id) : null,
        nombre_cliente: formData.nombre_cliente, cedula_cliente: formData.cedula_cliente,
        direccion_cliente: formData.direccion_cliente, telefono_cliente: formData.telefono_cliente,
        correo_cliente: formData.correo_cliente, proyecto_nombre: formData.proyecto_nombre,
        apartamento: formData.apartamento, direccion_proyecto: formData.direccion_proyecto,
        valor_contrato: parseFloat(formData.valor_contrato) || 0,
        valor_separacion: parseFloat(formData.valor_separacion) || 0,
        valor_inicio_obra: parseFloat(formData.valor_inicio_obra) || 0,
        valor_avance_obra: parseFloat(formData.valor_avance_obra) || 0,
        valor_saldo_final: parseFloat(formData.valor_saldo_final) || 0,
        valor_entrega: parseFloat(formData.valor_entrega) || 0,
        plazo_ejecucion: parseInt(formData.plazo_ejecucion) || 60,
        tipo_contrato: formData.tipo_contrato || 'A',
      };

      let gqlR, payload;
      if (contratoEditando && contratoEditando.id) {
        gqlR = await client.mutate(
          `mutation($id: Int!, $input: CrearContratoInput!) { actualizarContrato(id: $id, input: $input) { success message id consecutivo } }`,
          { id: contratoEditando.id, input }
        );
        payload = gqlR.actualizarContrato;
      } else {
        gqlR = await client.mutate(
          `mutation($input: CrearContratoInput!) { crearContrato(input: $input) { success message id consecutivo } }`,
          { input }
        );
        payload = gqlR.crearContrato;
      }

      if (payload.success) {
        success(contratoEditando ? 'Contrato actualizado' : 'Contrato guardado');
        cargarContratos();
        setVista('lista');
        resetForm();
      } else {
        error(payload.message || 'Error al guardar');
      }
    } catch (err) {
      error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const editarContrato = (contrato) => {
    const total = parseFloat(contrato.valor_contrato) || 0;
    setContratoEditando(contrato);

    // Tipo guardado (los contratos viejos no lo tienen → Tipo A)
    const tipo = ['A', 'B'].includes(contrato.tipo_contrato) ? contrato.tipo_contrato : 'A';
    const items = FORMA_PAGO[tipo];
    // ¿El contrato tiene valores de pago guardados? Si no (contrato antiguo sin
    // desglose), se reparte con los porcentajes por defecto de su tipo.
    const sumaGuardada = items.reduce((s, it) => s + (parseFloat(contrato[`valor_${it.key}`]) || 0), 0);
    const tieneDesglose = sumaGuardada > 0;

    // pct_* y valor_* de TODAS las claves; las que el tipo no usa quedan en 0.
    const pcts = {};
    const valores = {};
    CLAVES_PAGO.forEach(k => {
      const item = items.find(i => i.key === k);
      if (!item) { pcts[`pct_${k}`] = 0; valores[`valor_${k}`] = 0; return; }
      const guardado = parseFloat(contrato[`valor_${k}`]) || 0;
      if (tieneDesglose) {
        pcts[`pct_${k}`] = total > 0 ? Math.round(guardado / total * 100) : item.pct;
        valores[`valor_${k}`] = contrato[`valor_${k}`] || 0;
      } else {
        pcts[`pct_${k}`] = item.pct;
        valores[`valor_${k}`] = Math.round(total * item.pct / 100);
      }
    });

    setFormData({
      fecha: contrato.fecha,
      proyecto_id: contrato.proyecto_id || '',
      nombre_cliente: contrato.nombre_cliente,
      cedula_cliente: contrato.cedula_cliente,
      direccion_cliente: contrato.direccion_cliente || '',
      telefono_cliente: contrato.telefono_cliente || '',
      correo_cliente: contrato.correo_cliente || '',
      proyecto_nombre: contrato.proyecto_nombre,
      apartamento: contrato.apartamento || '',
      direccion_proyecto: contrato.direccion_proyecto || '',
      valor_contrato: contrato.valor_contrato,
      ...pcts,
      ...valores,
      plazo_ejecucion: contrato.plazo_ejecucion || '60',
      tipo_contrato: tipo
    });
    setCuerpoContrato(contrato.cuerpo_html || null);
    setVista('editar');
  };

  const descargarPDF = async () => {
    const previewElement = documentRef.current;

    if (!previewElement) {
      error('Error: No se encontró la vista previa');
      return;
    }

    // Obtener todas las páginas
    const pages = previewElement.querySelectorAll('.contrato-page');
    if (pages.length === 0) {
      error('Error: No se encontraron páginas');
      return;
    }

    try {
      // Crear PDF con jsPDF directamente
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        unit: 'px',
        format: [612, 792],
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      });

      // Procesar cada página
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];

        // Capturar página con html2canvas
        const html2canvasModule = await import('html2canvas');
        const html2canvas = html2canvasModule.default;

        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          width: 612,
          height: 792
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.98);

        // Añadir página (excepto la primera)
        if (i > 0) {
          pdf.addPage([612, 792], 'portrait');
        }

        // Añadir imagen a la página
        pdf.addImage(imgData, 'JPEG', 0, 0, 612, 792);
      }

      // Guardar PDF
      pdf.save(`CONTRATO-${formData.nombre_cliente}-${formData.proyecto_nombre}-${formData.apartamento}.pdf`);
      success('PDF descargado correctamente');
    } catch (err) {
      console.error('Error PDF:', err);
      error('Error al generar PDF');
    }
  };

  // Filtrar cotizaciones por búsqueda
  const cotizacionesFiltradas = cotizaciones.filter(c => {
    if (!busquedaCotizacion) return true;
    const texto = busquedaCotizacion.toLowerCase();
    return (c.nombre_cliente || '').toLowerCase().includes(texto) ||
           (c.proyecto_nombre || '').toLowerCase().includes(texto) ||
           (c.apartamento || '').toLowerCase().includes(texto) ||
           String(c.consecutivo).includes(texto);
  });

  const descargarPDFConCotizacion = async (cotizacionId) => {
    setShowCotizacionModal(false);
    setBusquedaCotizacion('');
    setGenerandoPDF(true);

    try {
      // 1. Cargar cotización completa
      const gqlData = await client.query(`query($id: Int!) { cotizacion(id: $id) { id consecutivo fecha nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 direccion habitaciones banos forma_cocina opciones_bano plano_url plano_nombre subtotal aiu_porcentaje aiu_valor iva_valor valor_final descuento valor_final_descuento actividades { id codigo descripcion capitulo categoria unidad cantidad valor_unitario valor_total espacio } plan_pagos { id descripcion porcentaje valor } } }`, { id: cotizacionId });
      const data = { success: !!gqlData.cotizacion, cotizacion: gqlData.cotizacion };

      if (!data.success || !data.cotizacion) {
        error('No se pudo cargar la cotización');
        setGenerandoPDF(false);
        return;
      }

      // 2. Renderizar CotizacionPDF oculto
      setCotizacionPDFData(data.cotizacion);

      // 3. Esperar render + carga de imágenes
      await new Promise(resolve => setTimeout(resolve, 2500));

      const previewElement = documentRef.current;
      if (!previewElement) {
        error('Error: No se encontró la vista previa del contrato');
        setGenerandoPDF(false);
        setCotizacionPDFData(null);
        return;
      }

      // 4. Generar PDF del contrato (mismas páginas)
      const pages = previewElement.querySelectorAll('.contrato-page');
      if (pages.length === 0) {
        error('Error: No se encontraron páginas del contrato');
        setGenerandoPDF(false);
        setCotizacionPDFData(null);
        return;
      }

      const { jsPDF } = await import('jspdf');
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;

      const pdf = new jsPDF({
        unit: 'px',
        format: [612, 792],
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      });

      // Páginas del contrato
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          width: 612,
          height: 792
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        if (i > 0) pdf.addPage([612, 792], 'portrait');
        pdf.addImage(imgData, 'JPEG', 0, 0, 612, 792);
      }

      // 5. Capturar cotización (página larga continua)
      const cotContainer = cotizacionPdfRef.current;
      if (cotContainer) {
        const cotPage = cotContainer.querySelector('.cotizacion-page');
        if (cotPage) {
          const cotCanvas = await html2canvas(cotPage, {
            scale: 2,
            useCORS: true,
            logging: false,
            letterRendering: true,
            width: 612,
            windowWidth: 612
          });

          const cotImgData = cotCanvas.toDataURL('image/jpeg', 0.98);
          const cotPageWidth = 612;
          const cotPageHeight = (cotCanvas.height * cotPageWidth) / cotCanvas.width;

          // Añadir como nueva página con altura completa de la cotización
          pdf.addPage([cotPageWidth, cotPageHeight], 'portrait');
          pdf.addImage(cotImgData, 'JPEG', 0, 0, cotPageWidth, cotPageHeight);
        }
      }

      // 6. Guardar PDF combinado
      pdf.save(`CONTRATO+COT-${formData.nombre_cliente}-${formData.proyecto_nombre}-${formData.apartamento}.pdf`);
      success('PDF con cotización descargado correctamente');
    } catch (err) {
      console.error('Error PDF:', err);
      error('Error al generar PDF con cotización');
    } finally {
      setGenerandoPDF(false);
      setCotizacionPDFData(null);
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const getDia = (fecha) => {
    if (!fecha) return 'DD';
    return new Date(fecha + 'T00:00:00').getDate();
  };

  const getMes = (fecha) => {
    if (!fecha) return 'MES';
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return meses[new Date(fecha + 'T00:00:00').getMonth()].toUpperCase();
  };

  const getAnio = (fecha) => {
    if (!fecha) return 'AAAA';
    return new Date(fecha + 'T00:00:00').getFullYear();
  };

  const numeroPalabras = (num) => {
    const unidades = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
    const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const n = parseInt(num) || 0;
    if (n === 0) return 'CERO';
    if (n >= 100) return n.toString();
    if (n < 10) return unidades[n];
    if (n >= 10 && n <= 19) return especiales[n - 10];
    if (n === 20) return 'VEINTE';
    if (n >= 21 && n <= 29) return 'VEINTI' + unidades[n - 20];
    const d = Math.floor(n / 10);
    const u = n % 10;
    return u === 0 ? decenas[d] : decenas[d] + ' Y ' + unidades[u];
  };

  return (
    <div className="contratos-container">
      {/* Header */}
      <div className="ct-header">
        <div className="ct-header-info">
          <h1>📄 Contratos</h1>
          <p>Genera y administra contratos de suministro e instalación</p>
        </div>
        <div className="ct-header-actions">
          {vista === 'lista' ? (
            <>
              {puedeEditarPlantilla && (
                <>
                  <button className="btn-editar-plantilla" onClick={() => abrirEditorPlantilla('A')} title="Editar la plantilla de cláusulas de TODOS los contratos Tipo A (5 pagos)">
                    ✏️ Editar plantilla (Tipo A)
                  </button>
                  <button className="btn-editar-plantilla" onClick={() => abrirEditorPlantilla('B')} title="Editar la plantilla de cláusulas de TODOS los contratos Tipo B (4 pagos)">
                    ✏️ Editar plantilla (Tipo B)
                  </button>
                </>
              )}
              <button className="btn-nuevo-contrato" onClick={() => setModalTipoAbierto(true)}>
                + Nuevo Contrato
              </button>
            </>
          ) : (
            <>
              {vista === 'editar' && puedeEditarPlantilla && (
                <button className="btn-editar-plantilla" onClick={() => setEditorAbierto('contrato')} title="Editar las cláusulas SOLO de este contrato">
                  ✏️ Editar este contrato
                </button>
              )}
              <button className="btn-volver" onClick={() => { setVista('lista'); resetForm(); }}>
                ← Volver al listado
              </button>
            </>
          )}
        </div>
      </div>

      {/* Vista Lista */}
      {vista === 'lista' && (
        <div className="ct-lista-view">
          {loading ? (
            <div className="ct-loading">Cargando...</div>
          ) : contratos.length === 0 ? (
            <div className="ct-empty">
              <span>📄</span>
              <p>No hay contratos generados</p>
              <button onClick={() => setModalTipoAbierto(true)}>Crear primer contrato</button>
            </div>
          ) : (
            <div className="ct-tabla">
              <div className="ct-busqueda-lista">
                <input
                  type="text"
                  placeholder="Buscar por consecutivo, cliente, proyecto, apartamento..."
                  value={busquedaLista}
                  onChange={(e) => setBusquedaLista(e.target.value)}
                />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Valor Contrato</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {contratos.filter(c => {
                    if (!busquedaLista) return true;
                    const q = busquedaLista.toLowerCase();
                    return (
                      String(c.consecutivo).includes(q) ||
                      (c.nombre_cliente || '').toLowerCase().includes(q) ||
                      (c.proyecto_nombre || '').toLowerCase().includes(q) ||
                      (c.apartamento || '').toLowerCase().includes(q)
                    );
                  }).map(contrato => (
                    <tr key={contrato.id}>
                      <td><span className="consecutivo">#{contrato.consecutivo}</span></td>
                      <td>{new Date(contrato.fecha + 'T00:00:00').toLocaleDateString('es-CO')}</td>
                      <td>
                        <div className="cliente-info">
                          <span className="nombre">{contrato.nombre_cliente}</span>
                          <span className="cedula">CC: {contrato.cedula_cliente}</span>
                        </div>
                      </td>
                      <td>
                        <div className="proyecto-info">
                          <span className="nombre">{contrato.proyecto_nombre}</span>
                          <span className="apto">{contrato.apartamento}</span>
                        </div>
                      </td>
                      <td className="monto">{formatMoney(contrato.valor_contrato)}</td>
                      <td className="acciones">
                        <button className="btn-editar" onClick={() => editarContrato(contrato)} title="Editar">
                          ✏️
                        </button>
                        <button className="btn-eliminar" onClick={async () => {
                          if (!window.confirm(`¿Eliminar contrato #${contrato.consecutivo}?`)) return;
                          try {
                            const r = await client.mutate(`mutation($id: Int!) { eliminarContrato(id: $id) { success message } }`, { id: contrato.id });
                            if (r.eliminarContrato.success) { success('Contrato eliminado'); cargarContratos(); }
                          } catch (err) { error('Error al eliminar'); }
                        }} title="Eliminar" style={{ marginLeft: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vista Crear/Editar */}
      {(vista === 'crear' || vista === 'editar') && (
        <div className="ct-crear-view">
          <div className="ct-grid">
            {/* Formulario */}
            <div className="ct-formulario">
              <h2>{vista === 'editar' ? 'Editar Contrato' : 'Nuevo Contrato'}</h2>

              <form onSubmit={handleSubmit}>
                {/* Copiar desde cotización */}
                <div className="form-section" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <h3 style={{ fontSize: '0.9rem', color: '#166534', margin: '0 0 8px 0' }}>Copiar datos desde cotización</h3>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={busquedaCotForm}
                      onChange={e => { setBusquedaCotForm(e.target.value); setShowCotFormDropdown(true); }}
                      onFocus={() => { if (busquedaCotForm) setShowCotFormDropdown(true); }}
                      onBlur={() => setTimeout(() => setShowCotFormDropdown(false), 200)}
                      placeholder="Buscar por # consecutivo, cliente, proyecto o apto..."
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #86efac', borderRadius: 6, fontSize: '0.88rem', background: '#fff' }}
                    />
                    {showCotFormDropdown && cotizacionesFormFiltradas.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                        background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                        maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      }}>
                        {cotizacionesFormFiltradas.map(cot => (
                          <div
                            key={cot.id}
                            onMouseDown={() => handleCopiarCotizacion(cot)}
                            style={{
                              padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>
                                COT-{String(cot.consecutivo).padStart(4, '0')} · {cot.nombre_cliente}
                              </div>
                              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                                {cot.proyecto_nombre}{cot.apartamento ? ` · Apt ${cot.apartamento}` : ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: '#059669', fontSize: '0.85rem' }}>
                                {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(cot.valor_final || 0)}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{cot.estado}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Datos del Proyecto */}
                <div className="form-section">
                  <h3>Datos del Proyecto</h3>
                  <div className="form-group">
                    <label>Seleccionar Proyecto</label>
                    <select name="proyecto_id" value={formData.proyecto_id} onChange={handleProyectoChange}>
                      <option value="">-- Seleccione o ingrese manualmente --</option>
                      {proyectos.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.proyecto_nombre} - {p.apartamento} ({p.nombre_cliente})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nombre Proyecto *</label>
                      <input type="text" name="proyecto_nombre" value={formData.proyecto_nombre} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Apartamento</label>
                      <input type="text" name="apartamento" value={formData.apartamento} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Dirección del Proyecto</label>
                    <input type="text" name="direccion_proyecto" value={formData.direccion_proyecto} onChange={handleChange} placeholder="Ej: Carrera 43A # 1 Sur - 100" />
                  </div>
                </div>

                {/* Datos del Cliente */}
                <div className="form-section">
                  <h3>Datos del Contratante</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nombre Completo *</label>
                      <input type="text" name="nombre_cliente" value={formData.nombre_cliente} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Cédula *</label>
                      <input type="text" name="cedula_cliente" value={formData.cedula_cliente} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Dirección de Residencia</label>
                    <input type="text" name="direccion_cliente" value={formData.direccion_cliente} onChange={handleChange} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Teléfono</label>
                      <input type="text" name="telefono_cliente" value={formData.telefono_cliente} onChange={handleChange} placeholder="+57 300 1234567" />
                    </div>
                    <div className="form-group">
                      <label>Correo Electrónico</label>
                      <input type="email" name="correo_cliente" value={formData.correo_cliente} onChange={handleChange} />
                    </div>
                  </div>
                </div>

                {/* Datos del Contrato */}
                <div className="form-section">
                  <h3>Datos del Contrato</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Fecha del Contrato *</label>
                      <input type="date" name="fecha" value={formData.fecha} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Plazo de Ejecución (días hábiles)</label>
                      <select name="plazo_ejecucion" value={formData.plazo_ejecucion} onChange={handleChange}>
                        <option value="35">35 días</option>
                        <option value="45">45 días</option>
                        <option value="60">60 días</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Tipo de Contrato</label>
                    <div className="ct-tipo-actual">
                      <span className={`ct-tipo-badge ${formData.tipo_contrato === 'B' ? 'ct-tipo-b' : ''}`}>
                        Tipo {formData.tipo_contrato || 'A'}
                      </span>
                      <span className="ct-tipo-actual-desc">
                        {formData.tipo_contrato === 'B'
                          ? '4 pagos · Separación · Inicio de obra · Avance de obra · Programar fecha de entrega'
                          : '5 pagos · Separación · Inicio de obra · Avance 1 · Avance 2 · Recibo a conformidad'}
                      </span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Valor Total del Contrato *</label>
                    <input type="number" name="valor_contrato" value={formData.valor_contrato} onChange={handleChange} placeholder="0" min="0" step="any" required />
                    {valorContratoLetras && <span className="valor-letras">{valorContratoLetras}</span>}
                  </div>
                </div>

                {/* Forma de Pago */}
                <div className="form-section">
                  <h3>Forma de Pago</h3>
                  <div className="ct-pago-grid">
                    {pagosDelTipo.map(item => (
                      <div className="ct-pago-item" key={item.key}>
                        <span className="ct-pago-label">{item.letra}. {item.label}</span>
                        <div className="ct-pago-controls">
                          <div className="ct-pago-pct">
                            <input type="number" value={formData[`pct_${item.key}`]} onChange={(e) => handlePctChange(`pct_${item.key}`, e.target.value)} min="0" max="100" step="1" />
                            <span>%</span>
                          </div>
                          <input type="number" className="ct-pago-valor-input" value={formData[`valor_${item.key}`]} onChange={(e) => handleValorChange(`valor_${item.key}`, e.target.value)} min="0" step="1" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Validación de forma de pago basada en valores */}
                  {formData.valor_contrato > 0 && (() => {
                    const total = parseFloat(formData.valor_contrato) || 0;
                    const sumaValores = pagosDelTipo.reduce(
                      (s, item) => s + (parseFloat(formData[`valor_${item.key}`]) || 0), 0);
                    const pctReal = total > 0 ? (sumaValores / total * 100) : 0;
                    const diferencia = sumaValores - total;
                    const esExacto = diferencia === 0;
                    return (
                      <div className={`ct-pago-validacion ${esExacto ? 'ct-pago-ok' : 'ct-pago-error'}`}>
                        <span className="ct-pago-validacion-icon">{esExacto ? '✓' : '⚠'}</span>
                        <span>
                          Suma de pagos: <strong>{formatMoney(sumaValores)}</strong> de <strong>{formatMoney(total)}</strong>
                          {' '}({pctReal.toFixed(2)}%)
                          {!esExacto && (
                            <span className="ct-pago-diferencia">
                              {' — '}{diferencia > 0 ? 'Excede' : 'Faltan'} <strong>{formatMoney(Math.abs(diferencia))}</strong>
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancelar" onClick={() => { setVista('lista'); resetForm(); }}>
                    Cancelar
                  </button>
                  <button type="button" className="btn-preview" onClick={descargarPDF} disabled={generandoPDF}>
                    {generandoPDF ? 'Generando...' : '📥 Descargar PDF'}
                  </button>
                  <button type="button" className="btn-preview ct-btn-pdf-cot" onClick={() => setShowCotizacionModal(true)} disabled={generandoPDF}>
                    📥 PDF + Cotización
                  </button>
                  <button type="submit" className="btn-guardar" disabled={loading}>
                    {loading ? 'Guardando...' : (vista === 'editar' ? 'Actualizar' : 'Guardar')}
                  </button>
                </div>
              </form>
            </div>

            {/* Vista previa del documento - 5 páginas */}
            <div className="ct-preview">
              <h2>Vista Previa del Contrato</h2>
              <div className="preview-container" ref={documentRef}>
                {/* PÁGINA 1 - Datos generales */}
                <div className="contrato-preview contrato-page">
                  <div className="doc-header">
                    <img src="/logo.png" alt="TUVI" className="doc-logo" />
                    <div className="doc-slogan">Facilidad • Seguridad • Calidad</div>
                  </div>

                  <div className="contrato-content">
                    <h2 className="contrato-titulo">CONTRATO DE SUMINISTRO E INSTALACIÓN DE MATERIALES Y ACABADOS</h2>

                    <table className="contrato-tabla">
                      <tbody>
                        <tr>
                          <td className="label">CONTRATISTA</td>
                          <td className="value bold">TUVI CONSTRUCCIONES S.A.S.</td>
                        </tr>
                        <tr>
                          <td className="label">IDENTIFICACIÓN</td>
                          <td className="value">901.771.781-1</td>
                        </tr>
                        <tr>
                          <td className="label">DATOS DE CONTACTO</td>
                          <td className="value">
                            Dirección: Carrera 22 # 17 – 325 OF. 954<br/>
                            Teléfono: 300 475 8672<br/>
                            Correo electrónico: tuviadministrativo@paralelogroup.com
                          </td>
                        </tr>
                        <tr>
                          <td className="label">CONTRATANTE</td>
                          <td className="value bold">{formData.nombre_cliente || 'NOMBRE CLIENTE'}</td>
                        </tr>
                        <tr>
                          <td className="label">IDENTIFICACIÓN</td>
                          <td className="value">C.C. {formData.cedula_cliente || 'CÉDULA CLIENTE'}</td>
                        </tr>
                        <tr>
                          <td className="label">DATOS DE CONTACTO</td>
                          <td className="value">
                            Dirección: {formData.direccion_cliente || 'Dirección residencia actual CLIENTE'}<br/>
                            Teléfono: {formData.telefono_cliente || '+57 (300) 0000000'}<br/>
                            Correo Electrónico: {formData.correo_cliente || 'correo@cliente.com'}
                          </td>
                        </tr>
                        <tr>
                          <td className="label">OBRA</td>
                          <td className="value">PROYECTO {formData.proyecto_nombre || 'NOMBRE PROYECTO'} {formData.apartamento && `Y ${formData.apartamento}`}</td>
                        </tr>
                        <tr>
                          <td className="label">UBICACIÓN</td>
                          <td className="value">{formData.direccion_proyecto || 'Dirección del proyecto'}</td>
                        </tr>
                        <tr>
                          <td className="label">FECHA DEL CONTRATO</td>
                          <td className="value">{getDia(formData.fecha)} de {getMes(formData.fecha)} de {getAnio(formData.fecha)}</td>
                        </tr>
                        <tr>
                          <td className="label">ALCANCE</td>
                          <td className="value">Suministro e instalación de materiales para acabados del Apartamento, bajo las cantidades, actividades indicadas en el Anexo No. 1 denominado "COTIZACIÓN".</td>
                        </tr>
                        <tr>
                          <td className="label">VALOR</td>
                          <td className="value">
                            <span className="bold">{valorContratoLetras || 'VALOR CONTRATO EN LETRAS'}</span> (<span className="bold">{formData.valor_contrato ? formatMoney(formData.valor_contrato) : '$VALOR EN NÚMEROS'}</span>), incluido el IVA sobre la Utilidad del AIU (Administración, Imprevistos y Utilidades) conforme a lo previsto en el artículo 462-1 del Estatuto Tributario y el artículo 1.3.1.7.9 del Decreto 1625 de 2016 y demás normas que lo adicionen, modifiquen o sustituyan.
                          </td>
                        </tr>
                        <tr>
                          <td className="label">FORMA DE PAGO</td>
                          <td className="value">
                            {pagosDelTipo.map((item, idx) => (
                              <React.Fragment key={item.key}>
                                <strong>{item.letra}.</strong> {item.label} ({formData[`pct_${item.key}`]}%): {formData[`valor_${item.key}`] ? formatMoney(formData[`valor_${item.key}`]) : '$VALOR'}
                                {idx < pagosDelTipo.length - 1 && <br/>}
                              </React.Fragment>
                            ))}
                          </td>
                        </tr>
                        <tr>
                          <td className="label">PLAZO DE EJECUCIÓN Y/O ENTREGA</td>
                          <td className="value">
                            <span className="bold">{numeroPalabras(formData.plazo_ejecucion)} ({formData.plazo_ejecucion || '60'}) DÍAS HÁBILES</span>, comprendidos de lunes a viernes, contados a partir de estos eventos: 1- Entrega de llaves, 2- Pago de anticipo, 3- firma de documento de especificaciones
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="doc-footer">
                    <span className="footer-empresa">Tuvi Construcciones S.A.S</span>
                    <div className="footer-contacto">
                      <span>tuviconstrucciones.com</span>
                      <span>@tuviacabados</span>
                    </div>
                  </div>
                </div>

                {/* Páginas de cláusulas (paginadas dinámicamente) + firmas.
                    El cuerpo sale de: contrato → plantilla global → plantilla por defecto. */}
                <ClausulasPaginadas
                  html={cuerpoImpreso}
                  firmaContent={
                    <>
                      <p className="firma-intro">
                        En constancia, se firma en Medellín, a los {getDia(formData.fecha)} días del mes de {getMes(formData.fecha)} de {getAnio(formData.fecha)}, en dos ejemplares del mismo tenor.
                      </p>
                      <div className="firmas-container">
                        <div className="firma-box">
                          <p className="firma-rol-titulo"><u>EL CONTRATISTA</u></p>
                          <img src="/firma juan esteban.png" alt="Firma" className="firma-img" />
                          <div className="firma-linea"></div>
                          <p className="firma-nombre"><strong>JUAN ESTEBAN DUQUE RIOS</strong></p>
                          <p className="firma-nombre"><strong>TUVI CONSTRUCCIONES S.A.S.</strong></p>
                          <p className="firma-cargo">NIT. 901.771.781-1</p>
                          <p className="firma-cargo">Representante Legal</p>
                          <p className="firma-cedula">C.C. 1.152.443.928</p>
                        </div>
                        <div className="firma-box">
                          <p className="firma-rol-titulo"><u>EL CONTRATANTE</u></p>
                          <div className="firma-espacio"></div>
                          <div className="firma-linea"></div>
                          <p className="firma-nombre"><strong>{formData.nombre_cliente || 'NOMBRES Y APELLIDOS CLIENTE'}</strong></p>
                          <p className="firma-cedula">C.C. {formData.cedula_cliente || 'NUMERO CEDULA CLIENTE'}</p>
                        </div>
                      </div>
                    </>
                  }
                />
              </div>
              <p className="preview-nota">* El PDF incluirá las 6 páginas completas del contrato</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal para seleccionar cotización */}
      {showCotizacionModal && (
        <div className="ct-modal-overlay" onClick={() => { setShowCotizacionModal(false); setBusquedaCotizacion(''); }}>
          <div className="ct-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ct-modal-header">
              <h3>Seleccionar Cotización</h3>
              <button className="ct-modal-close" onClick={() => { setShowCotizacionModal(false); setBusquedaCotizacion(''); }}>×</button>
            </div>
            <div className="ct-modal-search">
              <input
                type="text"
                value={busquedaCotizacion}
                onChange={(e) => setBusquedaCotizacion(e.target.value)}
                placeholder="Buscar por cliente, proyecto, consecutivo..."
                autoFocus
              />
            </div>
            <div className="ct-modal-list">
              {cotizacionesFiltradas.length === 0 ? (
                <div className="ct-modal-empty">No se encontraron cotizaciones</div>
              ) : (
                cotizacionesFiltradas.map(cot => (
                  <div key={cot.id} className="ct-modal-item" onClick={() => descargarPDFConCotizacion(cot.id)}>
                    <span className="ct-modal-item-num">#{cot.consecutivo}</span>
                    <div className="ct-modal-item-info">
                      <span className="ct-modal-item-main">{cot.proyecto_nombre} {cot.apartamento ? `- ${cot.apartamento}` : ''}</span>
                      <span className="ct-modal-item-sub">{cot.nombre_cliente}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contenedor oculto para renderizar CotizacionPDF */}
      {cotizacionPDFData && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <CotizacionPDF ref={cotizacionPdfRef} cotizacion={cotizacionPDFData} todosLosEspacios={[]} />
        </div>
      )}

      {/* Editor de cláusulas (plantilla global o contrato individual) */}
      {editorAbierto && (
        <EditorRico
          titulo={editorAbierto === 'plantilla' ? `Editar plantilla de contratos · Tipo ${plantillaTipo}` : `Editar cláusulas · Contrato #${contratoEditando?.consecutivo || ''}`}
          descripcion={editorAbierto === 'plantilla'
            ? `Estos cambios aplican a TODOS los contratos Tipo ${plantillaTipo} que no tengan un texto propio.`
            : 'Estos cambios aplican SOLO a este contrato.'}
          initialHtml={editorAbierto === 'plantilla'
            ? ((plantillaTipo === 'B' ? plantillaB : plantillaA) || CLAUSULAS_CONTRATO_DEFAULT)
            : cuerpoEfectivo}
          onGuardar={guardarDesdeEditor}
          onCancelar={() => setEditorAbierto(null)}
          guardando={guardandoEditor}
        />
      )}

      {/* Modal: elegir tipo de contrato antes de crear */}
      {modalTipoAbierto && (
        <div className="ct-tipo-overlay" onClick={() => setModalTipoAbierto(false)}>
          <div className="ct-tipo-modal" onClick={(e) => e.stopPropagation()}>
            <h3>¿Qué tipo de contrato vas a generar?</h3>
            <p className="ct-tipo-sub">La diferencia está en la forma de pago. Podrás cambiarlo después dentro del formulario.</p>

            <div className="ct-tipo-opciones">
              <button type="button" className="ct-tipo-card" onClick={() => iniciarContrato('A')}>
                <span className="ct-tipo-badge">Tipo A</span>
                <span className="ct-tipo-desc">5 pagos</span>
                <ul>
                  <li><b>10%</b> Separación</li>
                  <li><b>40%</b> Inicio de obra</li>
                  <li><b>20%</b> Avance de obra 1</li>
                  <li><b>20%</b> Avance de obra 2</li>
                  <li><b>10%</b> Recibo a conformidad</li>
                </ul>
              </button>

              <button type="button" className="ct-tipo-card" onClick={() => iniciarContrato('B')}>
                <span className="ct-tipo-badge ct-tipo-b">Tipo B</span>
                <span className="ct-tipo-desc">4 pagos</span>
                <ul>
                  <li><b>10%</b> Separación</li>
                  <li><b>40%</b> Inicio de obra</li>
                  <li><b>30%</b> Avance de obra</li>
                  <li><b>20%</b> Programar fecha de entrega</li>
                </ul>
              </button>
            </div>

            <div className="ct-tipo-acciones">
              <button type="button" className="btn-cancelar" onClick={() => setModalTipoAbierto(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contratos;
